import React, { useMemo, useState, useRef } from 'react';
import { Upload, FileText, CheckCircle, XCircle, RefreshCw, AlertCircle, Save, Landmark } from 'lucide-react';
import api from 'services/api';
import NotificationToast from 'components/common/NotificationToast';
import LoadingSpinner from 'components/common/LoadingSpinner';
import { COMPANY_STORAGE_KEY } from 'types/company.js';
import ConciliacaoDetalhesModal from './ConciliacaoDetalhesModal';
import ConciliacaoManualModal from './ConciliacaoManualModal';

const ConciliacaoBancaria = () => {
  const [arquivoOFX, setArquivoOFX] = useState(null);
  const [arquivoNome, setArquivoNome] = useState('');
  const [validando, setValidando] = useState(false);
  const [dadosConciliacao, setDadosConciliacao] = useState([]);
  const [conciliadas, setConciliadas] = useState([]);
  const [pendenciasOfx, setPendenciasOfx] = useState([]);
  const [pendenciasApi, setPendenciasApi] = useState([]);
  const [duplicidades, setDuplicidades] = useState([]);
  const [periodo, setPeriodo] = useState(null);
  const [estatisticas, setEstatisticas] = useState(null);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(100);
  const [abaAtiva, setAbaAtiva] = useState('conciliadas');
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [termoPesquisa, setTermoPesquisa] = useState('');
  const [termoPesquisaInput, setTermoPesquisaInput] = useState('');
  const [notification, setNotification] = useState(null);
  const [modalAberto, setModalAberto] = useState(false);
  const [itemSelecionado, setItemSelecionado] = useState(null);
  const [modalContaAberto, setModalContaAberto] = useState(false);
  const [contasDisponiveis, setContasDisponiveis] = useState([]);
  const [identificadoresOfx, setIdentificadoresOfx] = useState(null);
  const [error, setError] = useState('');
  const [mensagemModalConta, setMensagemModalConta] = useState('');
  const [conciliacaoId, setConciliacaoId] = useState(null);
  const [versaoAtual, setVersaoAtual] = useState(null);
  const [conciliacoesManuais, setConciliacoesManuais] = useState([]);
  const [estatisticasAntes, setEstatisticasAntes] = useState(null);
  const [idContaSelecionada, setIdContaSelecionada] = useState(null);
  const [dadosContaSelecionada, setDadosContaSelecionada] = useState(null);
  const [modalManualAberto, setModalManualAberto] = useState(false);
  const [itemOfxParaManual, setItemOfxParaManual] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [conciliacaoIdOrigem, setConciliacaoIdOrigem] = useState(null);
  const [versaoOrigem, setVersaoOrigem] = useState(null);
  const fileInputRef = useRef(null);

  // Função auxiliar para criar arquivo a partir de base64
  const criarArquivoDeBase64 = (base64String, nomeArquivo) => {
    try {
      // Decodificar base64
      const byteCharacters = atob(base64String);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/x-ofx' });
      return new File([blob], nomeArquivo, { type: 'application/x-ofx' });
    } catch (error) {
      console.error('Erro ao criar arquivo de base64:', error);
      throw error;
    }
  };

  // Função para carregar dados de uma Conciliação para edição
  const carregarDadosParaEdicao = async (conciliacaoId) => {
    try {
      setValidando(true);
      setError('');

      const response = await api.get(`/n8n/administracao/conciliacao-bancaria/${conciliacaoId}/carregar-para-edicao`);

      if (!response.data || !response.data.success) {
        const errorMessage = response.data?.message || 'Erro ao carregar dados para edição';
        setError(errorMessage);
        showNotification(errorMessage, 'error');
        return;
      }

      const data = response.data.data;

      // Definir ID da Conciliação original
      setConciliacaoIdOrigem(data.id);
      setVersaoOrigem(data.versao);

      // Carregar dados da conta
      setIdContaSelecionada(data.id_conta_bancaria);
      setDadosContaSelecionada(data.dados_conta);

      // Carregar Período
      setPeriodo({
        data_inicio: data.periodo_inicio,
        data_fim: data.periodo_fim
      });

      // Carregar identificadores OFX
      setIdentificadoresOfx(data.identificadores_ofx);

      // Recriar arquivo OFX a partir de base64
      if (data.arquivo_ofx_base64 && data.nome_arquivo_ofx) {
        try {
          const arquivoRecriado = criarArquivoDeBase64(data.arquivo_ofx_base64, data.nome_arquivo_ofx);
          setArquivoOFX(arquivoRecriado);
          setArquivoNome(data.nome_arquivo_ofx);
        } catch (error) {
          console.error('Erro ao recriar arquivo OFX:', error);
          showNotification('Erro ao recriar arquivo OFX. A edição pode continuar sem o arquivo.', 'warning');
        }
      }

      // Carregar linhas por categoria
      const linhasPorCategoria = data.linhas_por_categoria || {};
      setConciliadas(linhasPorCategoria.conciliadas || []);
      setPendenciasApi(linhasPorCategoria.pendencias_api || []);
      setPendenciasOfx(linhasPorCategoria.pendencias_ofx || []);
      setDuplicidades(linhasPorCategoria.duplicidades || []);

      // Carregar todas as linhas no dadosConciliacao
      const todasLinhas = data.snapshot.linhas || [];
      setDadosConciliacao(todasLinhas);

      // Calcular estatísticas completas a partir das linhas
      // Contar IXC - Pagas (linhas com tem_no_ixc=true e tipo_conta='paga' ou tipo DEBIT)
      const totalIxcPagas = todasLinhas.filter(l => 
        l.tem_no_ixc && (
          l.ixc?.tipo_conta === 'paga' || 
          l.tipo?.toUpperCase() === 'DEBIT' || 
          l.ixc?.tipo === 'DEBIT'
        )
      ).length;
      
      // Contar IXC - Recebidas (linhas com tem_no_ixc=true e tipo_conta='recebida' ou tipo CREDIT)
      const totalIxcRecebidas = todasLinhas.filter(l => 
        l.tem_no_ixc && (
          l.ixc?.tipo_conta === 'recebida' || 
          l.tipo?.toUpperCase() === 'CREDIT' || 
          l.ixc?.tipo === 'CREDIT'
        )
      ).length;

      // Contar OFX - Transações (linhas com tem_no_ofx=true)
      const totalOfx = todasLinhas.filter(l => l.tem_no_ofx).length;
      
      // Total Conciliado (linhas conciliadas)
      // Calcular diretamente das linhas para garantir que funcione mesmo se o backend retornar array associativo
      const totalConciliado = todasLinhas.filter(l => 
        (l.categoria || '').toLowerCase() === 'conciliadas'
      ).length;

      // Criar objeto de estatísticas completo
      const estatisticasCompletas = {
        ...(data.snapshot.estatisticas || {}),
        total_ixc_pagas: totalIxcPagas,
        total_ixc_recebidas: totalIxcRecebidas,
        total_ofx: totalOfx,
        total_conciliado: totalConciliado,
        total_pendencias_api: linhasPorCategoria.pendencias_api?.length || 0,
        total_pendencias_ofx: linhasPorCategoria.pendencias_ofx?.length || 0,
        total_duplicidades: linhasPorCategoria.duplicidades?.length || 0
      };

      // Carregar estatísticas
      setEstatisticas(estatisticasCompletas);
      setEstatisticasAntes(data.estatisticas_antes || null);

      // Carregar Conciliações manuais
      setConciliacoesManuais(data.conciliacoes_manuais || []);

      showNotification(`Dados da versão ${data.versao} carregados para edição`, 'success');
    } catch (err) {
      console.error('Erro ao carregar dados para edição:', err);
      const errorMessage = err.response?.data?.message || 'Erro ao carregar dados para edição';
      setError(errorMessage);
      showNotification(errorMessage, 'error');
    } finally {
      setValidando(false);
    }
  };

  // Verificar se há conciliacao_id na URL ou localStorage para carregar automaticamente
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const conciliacaoIdParam = urlParams.get('editar');
    const conciliacaoIdLocalStorage = localStorage.getItem('conciliacao_editar_id');
    
    const conciliacaoId = conciliacaoIdParam || conciliacaoIdLocalStorage;
    
    if (conciliacaoId) {
      carregarDadosParaEdicao(parseInt(conciliacaoId));
      // Limpar parâmetro da URL e localStorage
      if (conciliacaoIdParam) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      if (conciliacaoIdLocalStorage) {
        localStorage.removeItem('conciliacao_editar_id');
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      setTermoPesquisa(termoPesquisaInput);
      setPaginaAtual(1);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [termoPesquisaInput]);

  const getSelectedCompanyName = () => {
    try {
      const ctx = localStorage.getItem(COMPANY_STORAGE_KEY);
      if (ctx) {
        const parsed = JSON.parse(ctx);
        if (parsed?.companies?.length > 0) {
          return parsed.companies[0].name;
        }
      }
    } catch {}
    return 'MOV Fibra';
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar extensão .ofx
    if (!file.name.toLowerCase().endsWith('.ofx')) {
      showNotification('Por favor, selecione um arquivo .OFX válido', 'error');
      e.target.value = '';
      return;
    }

    setArquivoOFX(file);
    setArquivoNome(file.name);
    setError('');
    e.target.value = '';
  };

  const handleFileSelectWithValidation = (e) => {
    const file = e.target.files?.[0];

    // Se não houver arquivo, mantém comportamento atual
    if (!file) {
      handleFileSelect(e);
      return;
    }

    // Validar tamanho máximo (10MB)
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    if (file.size > MAX_FILE_SIZE) {
      showNotification('O arquivo OFX deve ter no máximo 10MB', 'error');
      e.target.value = '';
      return;
    }

    // Se passou na validação de tamanho, segue fluxo normal
    handleFileSelect(e);
  };

  const removerArquivo = () => {
    setArquivoOFX(null);
    setArquivoNome('');
    setDadosConciliacao([]);
    setConciliadas([]);
    setPendenciasOfx([]);
    setPendenciasApi([]);
    setDuplicidades([]);
    setPeriodo(null);
    setEstatisticas(null);
    setPaginaAtual(1);
    setAbaAtiva('conciliadas');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const processarArquivoOFX = async (idContaSelecionada = null) => {
    if (!arquivoOFX) {
      showNotification('Por favor, selecione um arquivo OFX primeiro', 'error');
      return;
    }

    setValidando(true);
    setError('');
    setDadosConciliacao([]);
    setConciliadas([]);
    setPendenciasOfx([]);
    setPendenciasApi([]);
    setDuplicidades([]);

    try {
      const formData = new FormData();
      formData.append('arquivo_ofx', arquivoOFX);
      formData.append('empresa', getSelectedCompanyName());
      if (idContaSelecionada) {
        formData.append('id_conta', idContaSelecionada);
      }

      const response = await api.post('/n8n/administracao/conciliacao-bancaria/processar-ofx', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const data = response.data;

      if (!data) {
        showNotification('Resposta inválida do servidor', 'error');
        setValidando(false);
        return;
      }

      // Verificar se é erro de conta não identificada (mesmo com status 200 do Axios)
      if (data.success === false && data.error_code === 'CONTA_NAO_IDENTIFICADA') {
        setContasDisponiveis(data.contas_disponiveis || []);
        setIdentificadoresOfx(data.identificadores_ofx || null);
        setMensagemModalConta(data.message || 'Não foi possível identificar a conta bancária do arquivo OFX. Por favor, selecione a conta manualmente.');
        setModalContaAberto(true);
        setValidando(false);
        return;
      }

      // Verificar se houve erro na resposta
      if (data.success === false) {
        const errorMessage = data.message || 'Erro ao processar arquivo OFX. Tente novamente.';
        setError(errorMessage);
        showNotification(errorMessage, 'error');
        setValidando(false);
        return;
      }

      const payload = data.dados || data;

      const hasNewShape =
        payload.conciliadas ||
        payload.pendencias_ofx ||
        payload.pendencias_api ||
        payload.duplicidades;

      if (hasNewShape) {
        const conciliadasData = payload.conciliadas || [];
        const pendenciasOfxData = payload.pendencias_ofx || [];
        const pendenciasApiData = payload.pendencias_api || [];
        const duplicidadesData = payload.duplicidades || [];

        setConciliadas(conciliadasData);
        setPendenciasOfx(pendenciasOfxData);
        setPendenciasApi(pendenciasApiData);
        setDuplicidades(duplicidadesData);

        const linhas = [];

        conciliadasData.forEach((item) => {
          const ixc = item.ixc || {};
          const ofx = item.ofx || {};

          const valor =
            ofx.valor ??
              ixc.valor ??
              ixc.valor_pago ??
              ixc.valor_recebido ??
              0;

          const dataLinha =
            ofx.data ??
              ixc.data_pagamento ??
              ixc.debito_data ??
              ixc.credito_data ??
              null;

          const fornecedor =
            item.nome ||
              ofx.nome ||
              ofx.descricao ||
              ixc.nome ||
              ixc.fornecedor ||
              '';

          const tipoLinha =
            (ofx.tipo || ixc.tipo || (valor < 0 ? 'DEBIT' : 'CREDIT')) ?? null;

          linhas.push({
            valor: Number(valor || 0),
            data: dataLinha,
            fornecedor,
            tem_no_ixc: true,
            tem_no_ofx: true,
            categoria: 'conciliadas',
            tipo: tipoLinha,
            ixc: ixc,
            ofx: ofx,
          });
        });

        pendenciasApiData.forEach((item) => {
          const conta = item.conta || {};
          const tipo = item.tipo;

          let valorConta = conta.valor;
          if (tipo === 'DEBIT') {
            valorConta = conta.valor_pago ?? conta.valor ?? 0;
          } else if (tipo === 'CREDIT') {
            valorConta = conta.valor_recebido ?? conta.valor ?? 0;
          }

          const dataLinha =
            conta.data_pagamento ??
            conta.debito_data ??
            conta.credito_data ??
            null;

          const fornecedor =
            item.nome ||
            conta.nome ||
              conta.fornecedor ||
              conta.cliente ||
              '';

          const tipoLinha = (tipo || conta.tipo || null) ?? null;

          linhas.push({
            valor: Number(valorConta || 0),
            data: dataLinha,
            fornecedor,
            tem_no_ixc: true,
            tem_no_ofx: false,
            categoria: 'pendencias_api',
            tipo: tipoLinha,
            ixc: conta,
            ofx: null
          });
        });

        pendenciasOfxData.forEach((item) => {
          const tx = item.transacao || {};

          const valor = tx.valor ?? 0;
          const dataLinha = tx.data || null;
          const fornecedor = item.nome || tx.nome || tx.descricao || '';
          const tipoLinha =
            (tx.tipo || (valor < 0 ? 'DEBIT' : 'CREDIT')) ?? null;

          linhas.push({
            valor: Number(valor || 0),
            data: dataLinha,
            fornecedor,
            tem_no_ixc: false,
            tem_no_ofx: true,
            categoria: 'pendencias_ofx',
            tipo: tipoLinha,
            ixc: null,
            ofx: tx
          });
        });

        duplicidadesData.forEach((dup) => {
          const contas = dup.contas || [];
          if (contas.length === 0) return;

          // Usar dados da primeira conta para exibição na lista
          const primeiraConta = contas[0];
          const valor =
            primeiraConta.valor_pago ?? primeiraConta.valor_recebido ?? primeiraConta.valor ?? 0;
          const dataLinha =
            primeiraConta.data_pagamento ??
            primeiraConta.debito_data ??
            primeiraConta.credito_data ??
            null;
          const fornecedor =
            dup.nome ||
              primeiraConta.nome ||
              primeiraConta.fornecedor ||
              primeiraConta.cliente ||
              '';

          // Priorizar tipo da duplicidade, depois da conta
          const tipoLinha =
            dup.tipo ||
              primeiraConta.tipo ||
              primeiraConta.tipo_titulo ||
              primeiraConta.tipo_conta ||
              null;

          linhas.push({
            valor: Number(valor || 0),
            data: dataLinha,
            fornecedor,
            tem_no_ixc: true,
            tem_no_ofx: false,
            categoria: 'duplicidades',
            tipo: tipoLinha,
            ixcContas: contas, // Array com todas as contas duplicadas
            ofx: null
          });
        });

        setDadosConciliacao(linhas);
        setPeriodo(data.periodo || null);
        setEstatisticas(data.estatisticas || null);

        // Armazenar estatísticas iniciais (antes de Conciliações manuais)
        if (data.estatisticas) {
          setEstatisticasAntes({
            pendencias_api: data.estatisticas.total_pendencias_api || 0,
            pendencias_ofx: data.estatisticas.total_pendencias_ofx || 0,
            duplicidades: data.estatisticas.total_duplicidades || 0,
            conciliadas: data.estatisticas.total_conciliado || 0
          });
        }
        
        // Atualizar idContaSelecionada se a conta foi reconhecida automaticamente
        if (data.id_conta && !idContaSelecionada) {
          setIdContaSelecionada(data.id_conta);
          // Se houver dados da conta na resposta, atualizar também
          if (data.dados_conta) {
            setDadosContaSelecionada(data.dados_conta);
          }
        } else if (data.id_conta && idContaSelecionada && !dadosContaSelecionada) {
          // Se já temos idContaSelecionada mas não temos dadosContaSelecionada
          // e a resposta trouxe dados_conta, atualizar
          if (data.dados_conta) {
            setDadosContaSelecionada(data.dados_conta);
          }
        }
        
        setPaginaAtual(1);
        setAbaAtiva('conciliadas');
        setConciliacaoId(null);
        setVersaoAtual(null);
        setConciliacoesManuais([]);
        showNotification('Arquivo OFX processado com sucesso!', 'success');
      } else if (response.data && response.data.success && response.data.dados) {
        const dadosAntigos = Array.isArray(response.data.dados) ? response.data.dados : [];
        setDadosConciliacao(dadosAntigos);
        setPeriodo(response.data.periodo || null);
        setEstatisticas(response.data.estatisticas || null);
        setPaginaAtual(1);
        setAbaAtiva('conciliadas');
        setError('');
        showNotification('Arquivo OFX processado com sucesso!', 'success');
      } else {
        showNotification('Resposta inválida do servidor', 'error');
      }
    } catch (err) {
      console.error('Erro ao processar arquivo OFX:', err);
      console.error('Detalhes do erro:', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.response?.data?.message
      });
      
      // Verificar se é erro de conta não identificada
      if (err.response?.data?.error_code === 'CONTA_NAO_IDENTIFICADA') {
        setContasDisponiveis(err.response.data.contas_disponiveis || []);
        setIdentificadoresOfx(err.response.data.identificadores_ofx || null);
        setMensagemModalConta(err.response.data.message || 'Não foi possível identificar a conta bancária do arquivo OFX. Por favor, selecione a conta manualmente.');
        setModalContaAberto(true);
        setValidando(false);
        return;
      }
      
      // Tratamento melhorado de erros
      let errorMessage = 'Erro ao processar arquivo OFX. Tente novamente.';
      
      // Priorizar mensagem do servidor
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.status === 403) {
        errorMessage = 'Acesso negado. Você não tem permissão para processar arquivos OFX. Entre em contato com o administrador do sistema.';
      } else if (err.response?.status === 401) {
        errorMessage = 'Sessão expirada. Por favor, faça login novamente.';
      } else if (err.response?.status === 400) {
        errorMessage = err.response?.data?.message || 'Dados inválidos. Verifique o arquivo OFX e tente novamente.';
      } else if (err.response?.status === 422) {
        const validationErrors = err.response.data?.errors;
        if (validationErrors) {
          const firstError = Object.values(validationErrors)[0];
          errorMessage = Array.isArray(firstError) ? firstError[0] : 'Arquivo OFX inválido. Verifique o formato e tente novamente.';
        } else {
          errorMessage = err.response?.data?.message || 'Arquivo OFX inválido. Verifique o formato e tente novamente.';
        }
      } else if (err.response?.status >= 500) {
        errorMessage = 'Erro interno do servidor. Tente novamente mais tarde.';
      } else if (err.code === 'NETWORK_ERROR' || !err.response) {
        errorMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
      }
      
      setError(errorMessage);
      showNotification(errorMessage, 'error');
      setDadosConciliacao([]);
      setPeriodo(null);
      setEstatisticas(null);
      setPaginaAtual(1);
    } finally {
      setValidando(false);
    }
  };

  const handleSelecionarConta = (idConta, dadosConta = null) => {
    setModalContaAberto(false);
    setMensagemModalConta('');
    setIdContaSelecionada(idConta);
    if (dadosConta) {
      setDadosContaSelecionada(dadosConta);
    }
    processarArquivoOFX(idConta);
  };

  const converterArquivoParaBase64 = (arquivo) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1]; // Remove data:application/...;base64,
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(arquivo);
    });
  };

  const salvarConciliacao = async () => {
    if (!conciliacaoId && !idContaSelecionada) {
      showNotification('Por favor, selecione uma conta bancária primeiro', 'error');
      return;
    }

    if (!estatisticasAntes) {
      showNotification('Estatísticas não disponíveis. Processe o arquivo OFX primeiro.', 'error');
      return;
    }

    setSalvando(true);
    try {
      const estatisticasDepois = {
        pendencias_api: pendenciasApi.length,
        pendencias_ofx: pendenciasOfx.length,
        duplicidades: duplicidades.length,
        conciliadas: conciliadas.length
      };

      // Converter arquivo OFX para base64 se existir
      let arquivoBase64 = null;
      if (arquivoOFX) {
        try {
          arquivoBase64 = await converterArquivoParaBase64(arquivoOFX);
        } catch (error) {
          console.error('Erro ao converter arquivo para base64:', error);
          showNotification('Erro ao processar arquivo OFX. Tente novamente.', 'error');
          setSalvando(false);
          return;
        }
      }

      // Preparar snapshot com linhas formatadas e estatísticas
      const snapshotLinhas = dadosConciliacao || [];
      const snapshotEstatisticas = {
        conciliadas: conciliadas.length,
        pendencias_api: pendenciasApi.length,
        pendencias_ofx: pendenciasOfx.length,
        duplicidades: duplicidades.length
      };

      const payload = {
        ...(conciliacaoIdOrigem && { conciliacao_id_origem: conciliacaoIdOrigem }),
        id_conta_bancaria: idContaSelecionada,
        banco: dadosContaSelecionada?.banco || null,
        conta: dadosContaSelecionada?.conta || null,
        data_conciliacao: new Date().toISOString().split('T')[0],
        periodo_inicio: periodo?.data_inicio || null,
        periodo_fim: periodo?.data_fim || null,
        estatisticas_antes: estatisticasAntes,
        estatisticas_depois: estatisticasDepois,
        nome_arquivo_ofx: arquivoNome,
        tamanho_arquivo_ofx: arquivoOFX?.size || null,
        identificadores_ofx: identificadoresOfx,
        arquivo_ofx_base64: arquivoBase64,
        snapshot_linhas: snapshotLinhas,
        snapshot_estatisticas: snapshotEstatisticas,
        conciliacoes_manuais: conciliacoesManuais.map(cm => ({
          id_conta_ixc: cm.id_conta_ixc,
          tipo_conta_ixc: cm.tipo_conta_ixc,
          dados_ixc: cm.dados_ixc,
          fitid_ofx: cm.fitid_ofx,
          dados_ofx: cm.dados_ofx,
          valor: cm.valor,
          data: cm.data,
          nome_normalizado: cm.nome_normalizado,
          observacao: cm.observacao
        }))
      };

      const response = await api.post('/n8n/administracao/conciliacao-bancaria/salvar', payload);

      // Verificar primeiro se houve erro mesmo com status 200
      if (response.data && response.data.success === false) {
        const errorMessage = response.data.message || 'Erro ao salvar Conciliação';
        console.error('Erro na resposta:', response.data);
        showNotification(errorMessage, 'error');
        return;
      }

      if (response.data && response.data.success) {
        setConciliacaoId(response.data.data.conciliacao_id);
        setVersaoAtual(response.data.data.versao);
        // Limpar estado de edição após salvar
        if (conciliacaoIdOrigem) {
          setConciliacaoIdOrigem(null);
          setVersaoOrigem(null);
        }
        const mensagem = conciliacaoIdOrigem 
          ? `Nova versão ${response.data.data.versao} criada com sucesso!` 
          : `Conciliação salva com sucesso! versão ${response.data.data.versao}`;
        showNotification(mensagem, 'success');
      } else {
        const errorMessage = response.data?.message || 'Erro ao salvar Conciliação';
        console.error('Resposta inesperada:', response.data);
        showNotification(errorMessage, 'error');
      }
    } catch (err) {
      console.error('Erro ao salvar Conciliação:', err);
      console.error('Detalhes do erro:', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.response?.data?.message
      });
      
      // Tratamento melhorado de erros
      let errorMessage = 'Erro ao salvar Conciliação. Tente novamente.';
      
      // Priorizar mensagem do servidor
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.status === 403) {
        errorMessage = 'Acesso negado. Você não tem permissão para salvar Conciliações. Entre em contato com o administrador do sistema.';
      } else if (err.response?.status === 401) {
        errorMessage = 'Sessão expirada. Por favor, faça login novamente.';
      } else if (err.response?.status === 422) {
        const validationErrors = err.response.data?.errors;
        if (validationErrors) {
          const firstError = Object.values(validationErrors)[0];
          errorMessage = Array.isArray(firstError) ? firstError[0] : 'Dados inválidos. Verifique as informações e tente novamente.';
        } else {
          errorMessage = 'Dados inválidos. Verifique as informações e tente novamente.';
        }
      } else if (err.response?.status >= 500) {
        errorMessage = 'Erro interno do servidor. Tente novamente mais tarde.';
      } else if (!err.response) {
        errorMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
      }
      
      showNotification(errorMessage, 'error');
    } finally {
      setSalvando(false);
    }
  };

  const formatarMoeda = (valor) => {
    if (!valor && valor !== 0) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const formatarData = (data) => {
    if (!data) return '-';
    try {
      // Data do IXC costuma vir como "DD/MM/YYYY HH:MM:SS"
      const matchBrComHora = data.match(/^(\d{2})\/(\d{2})\/(\d{4})\s+\d{2}:\d{2}:\d{2}$/);
      if (matchBrComHora) {
        return `${matchBrComHora[1]}/${matchBrComHora[2]}/${matchBrComHora[3]}`;
      }

      // Datas no formato brasileiro sem hora (DD/MM/YYYY)
      const matchBr = data.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (matchBr) {
        return data;
      }

      // Datas no formato ISO (YYYY-MM-DD) - parsear manualmente para evitar timezone
      const matchIso = data.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (matchIso) {
        return `${matchIso[3]}/${matchIso[2]}/${matchIso[1]}`;
      }

      const date = new Date(data);
      if (Number.isNaN(date.getTime())) {
        return data;
      }

      return date.toLocaleDateString('pt-BR');
    } catch {
      return data;
    }
  };

  const renderTipoBadge = (tipo) => {
    if (!tipo) return null;
    const normalizado = tipo.toUpperCase();
    const isDebit = normalizado === 'DEBIT';
    const classes = isDebit
      ? 'bg-[color:var(--surface-muted)] text-[color:var(--status-danger)] border-[color:var(--border)]'
      : 'bg-[color:var(--surface-muted)] text-[color:var(--status-good)] border-[color:var(--border)]';
    const label = isDebit ? 'Débito' : 'Crédito';

    return (
      <span className={`px-2 py-0.5 text-xs font-semibold rounded-full border ${classes}`}>
        {label}
      </span>
    );
  };

  const abrirDetalhes = (item) => {
    setItemSelecionado(item);
    setModalAberto(true);
  };

  const abrirModalManual = (item, e) => {
    e?.stopPropagation();
    setItemOfxParaManual(item);
    setModalManualAberto(true);
  };

  const abrirModalManualPendenciasApi = (item, e) => {
    e?.stopPropagation();
    // Criar uma transação OFX fictícia baseada na conta IXC
    const ofxFicticio = {
      valor: item.valor,
      data: item.data,
      descricao: item.fornecedor || '',
      nome: item.fornecedor || '',
      tipo: item.tipo || (item.valor < 0 ? 'DEBIT' : 'CREDIT'),
      fitid: null // Não tem FITID pois é fictício
    };
    setItemOfxParaManual({ ofx: ofxFicticio, ixc: item.ixc, isPendenciaApi: true });
    setModalManualAberto(true);
  };

  const handleConciliacaoManualConfirmada = (conciliacaoManual) => {
    // Adicionar à lista de Conciliações manuais
    setConciliacoesManuais([...conciliacoesManuais, conciliacaoManual]);
    
    const isPendenciaApi = itemOfxParaManual?.isPendenciaApi;

    // Verificar se a Conciliação manual resolve tanto uma pendência API quanto uma OFX
    const temFitidOfx = conciliacaoManual.fitid_ofx && conciliacaoManual.fitid_ofx !== null && conciliacaoManual.fitid_ofx !== '';
    const temIdContaIxc = conciliacaoManual.id_conta_ixc && conciliacaoManual.id_conta_ixc !== null;

    // Se tem ambos, significa que está resolvendo uma pendência API E uma pendência OFX
    const resolveAmbas = temFitidOfx && temIdContaIxc;

    // Remover da lista de Pendências API se for pendência API ou se resolve ambas
    if (isPendenciaApi || resolveAmbas) {
      const novasPendenciasApi = pendenciasApi.filter(
        p => (p.conta?.id !== conciliacaoManual.id_conta_ixc && p.ixc?.id !== conciliacaoManual.id_conta_ixc)
      );
      setPendenciasApi(novasPendenciasApi);
    }

    // Remover da lista de Pendências OFX se for pendência OFX ou se resolve ambas
    if (!isPendenciaApi || resolveAmbas) {
      const novasPendenciasOfx = pendenciasOfx.filter(
        p => p.transacao?.fitid !== conciliacaoManual.fitid_ofx && p.ofx?.fitid !== conciliacaoManual.fitid_ofx
      );
      setPendenciasOfx(novasPendenciasOfx);
    }
    
    // Adicionar as conciliadas
    const novaConciliada = {
      nome: conciliacaoManual.nome_normalizado || '',
      ixc: conciliacaoManual.dados_ixc,
      ofx: conciliacaoManual.dados_ofx
    };
    setConciliadas([...conciliadas, novaConciliada]);

    // Recalcular dados de Conciliação - remover de ambas as categorias se ambas foram resolvidas
    let novasLinhas = dadosConciliacao;

    // Remover pendência API se aplicável
    if (isPendenciaApi || resolveAmbas) {
      novasLinhas = novasLinhas.filter(
        d => !(d.categoria === 'pendencias_api' && d.ixc?.id === conciliacaoManual.id_conta_ixc)
      );
    }

    // Remover pendência OFX se aplicável
    if (!isPendenciaApi || resolveAmbas) {
      novasLinhas = novasLinhas.filter(
        d => !(d.categoria === 'pendencias_ofx' && (d.ofx?.fitid === conciliacaoManual.fitid_ofx || d.transacao?.fitid === conciliacaoManual.fitid_ofx))
      );
    }
    
    novasLinhas.push({
      valor: conciliacaoManual.valor,
      data: conciliacaoManual.data,
      fornecedor: conciliacaoManual.nome_normalizado || '',
      tem_no_ixc: true,
      tem_no_ofx: true,
      categoria: 'conciliadas',
      tipo: conciliacaoManual.dados_ofx?.tipo || (conciliacaoManual.valor < 0 ? 'DEBIT' : 'CREDIT'),
      ixc: conciliacaoManual.dados_ixc,
      ofx: conciliacaoManual.dados_ofx,
      manual: true
    });
    setDadosConciliacao(novasLinhas);

    showNotification('Conciliação manual adicionada com sucesso!', 'success');
  };

  const abas = [
    { id: 'conciliadas', label: 'Conciliadas', count: conciliadas.length },
    { id: 'pendencias_api', label: 'Pendências API', count: pendenciasApi.length },
    { id: 'pendencias_ofx', label: 'Pendências OFX', count: pendenciasOfx.length },
    { id: 'duplicidades', label: 'Duplicidades', count: duplicidades.length }
  ];

  const dadosFiltrados = useMemo(() => {
    return dadosConciliacao
      .filter((item) => item.categoria === abaAtiva)
      .filter((item) => {
        if (filtroTipo === 'todos') return true;
        const tipo = (item.tipo || '').toUpperCase();
        if (filtroTipo === 'credito') return tipo === 'CREDIT';
        if (filtroTipo === 'debito') return tipo === 'DEBIT';
        return true;
      })
      .filter((item) => {
        if (!termoPesquisa.trim()) return true;
        const termo = termoPesquisa.toLowerCase().trim();
        const fornecedor = (item.fornecedor || '').toLowerCase();
        const valor = String(item.valor || '');
        return fornecedor.includes(termo) || valor.includes(termo);
      })
      .sort((a, b) => {
        const dataA = a.data ? new Date(a.data) : new Date(0);
        const dataB = b.data ? new Date(b.data) : new Date(0);
        return dataA - dataB;
      });
  }, [dadosConciliacao, abaAtiva, filtroTipo, termoPesquisa]);

  const totalRegistros = dadosFiltrados.length;
  const totalPaginas = Math.max(1, Math.ceil(totalRegistros / tamanhoPagina));
  const indiceInicio = (paginaAtual - 1) * tamanhoPagina;
  const indiceFim = indiceInicio + tamanhoPagina;
  const dadosPagina = dadosFiltrados.slice(indiceInicio, indiceFim);

  return (
    <div>
      {notification && (
        <NotificationToast 
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Componente único integrado - design fluido */}
      <div className="bg-[color:var(--surface)] rounded-b-lg shadow-sm border border-t-0 border-[color:var(--border)] overflow-hidden">
        {/* Seção de Upload - sem bordas separadoras */}
        <div className="p-6">
          {/* Indicador de modo de edição */}
          {conciliacaoIdOrigem && (
            <div className="mb-4 rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-muted)] p-3 flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-[color:var(--accent)] flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-[color:var(--text-primary)]">
                  Editando versão {versaoOrigem} da Conciliação
                </p>
                <p className="text-xs text-[color:var(--text-primary)] mt-1">
                  Ao salvar, será criada uma nova versão. A versão original não será alterada.
                </p>
              </div>
            </div>
          )}
          
          <div className="mb-6">
            <p className="text-[color:var(--text-muted)]">
              Faça o upload do arquivo OFX e valide com os dados do IXC
            </p>
          </div>

          {/* Mensagem de erro */}
          {error && (
            <div className="mb-4 rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-muted)] p-3">
              <p className="text-sm text-[color:var(--status-danger)]">{error}</p>
            </div>
          )}

          {/* Upload de arquivo */}
          <div>
            <label className="block text-sm font-medium text-[color:var(--text-primary)] mb-2">
              Arquivo OFX
            </label>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                {arquivoOFX ? (
                  <div className="flex items-center justify-between p-4 bg-[color:var(--surface-muted)] border border-[color:var(--border)] rounded-lg hover:shadow-sm transition-shadow">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-[color:var(--accent)]" />
                      <div>
                        <p className="text-sm font-medium text-[color:var(--text-primary)]">{arquivoNome}</p>
                        <p className="text-xs text-[color:var(--text-muted)]">
                          {(arquivoOFX.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={removerArquivo}
                      className="p-1 rounded-full text-[color:var(--status-danger)] hover:bg-[color:var(--surface-muted)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/40"
                      title="Remover arquivo"
                    >
                      <XCircle className="h-5 w-5" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-[color:var(--border)] rounded-lg cursor-pointer bg-[color:var(--surface-muted)] hover:border-[color:var(--accent)] transition-all duration-200">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Upload className="h-8 w-8 text-[color:var(--text-muted)] mb-2" />
                      <p className="mb-2 text-sm text-[color:var(--text-muted)]">
                        <span className="font-semibold">Clique para fazer upload</span> ou arraste o arquivo
                      </p>
                      <p className="text-xs text-[color:var(--text-muted)]">OFX (máx. 10MB)</p>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".ofx"
                      onChange={handleFileSelectWithValidation}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
              {arquivoOFX && dadosConciliacao.length === 0 && !validando && (
                <button
                  onClick={() => processarArquivoOFX()}
                  className="px-6 py-3 rounded-lg bg-[color:var(--accent)] text-white hover:opacity-90 flex items-center gap-2 font-medium transition-all shadow-sm hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/45"
                >
                  <CheckCircle className="h-4 w-4" />
                  Processar e Validar
                </button>
              )}
              {validando && (
                <div className="px-6 py-3 bg-[color:var(--accent)] text-white rounded-lg opacity-75 flex items-center gap-2 font-medium shadow-sm">
                  Processando
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tabela de Conciliação - transição suave sem bordas */}
        {dadosConciliacao.length > 0 && (
          <>
            <div className="sticky top-0 z-20 bg-[color:var(--surface)]/95 px-6 pt-4 pb-6 backdrop-blur-sm md:static md:bg-[color:var(--surface)]">
              {/* Header com versão, salva e Período - layout melhorado */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    {versaoAtual && (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-[color:var(--surface-muted)] text-[color:var(--text-primary)] border border-[color:var(--border)]">
                        versão {versaoAtual}
                      </span>
                    )}
                    {conciliacaoId && (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-[color:var(--surface-muted)] text-[color:var(--status-good)] border border-[color:var(--border)]">
                        Salva
                      </span>
                    )}
                    {dadosContaSelecionada && (
                      <span className="px-3 py-1 text-xs font-medium rounded-full bg-[color:var(--surface-muted)] text-[color:var(--text-primary)] border border-[color:var(--border)] flex items-center gap-1">
                        <Landmark className="h-3 w-3" />
                        {dadosContaSelecionada.nome || dadosContaSelecionada.descricao || `Conta ${dadosContaSelecionada.id}`}
                        {dadosContaSelecionada.banco && (
                          <span className="text-[color:var(--text-muted)]">
                            : {dadosContaSelecionada.banco}
                            {dadosContaSelecionada.conta && ` - Cc: ${dadosContaSelecionada.conta}`}
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-[color:var(--text-muted)] mb-2">
                    {dadosConciliacao.length} registro(s) encontrado(s)
                  </p>
                  {totalRegistros > 0 && (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 text-sm text-[color:var(--text-muted)]">
                      <span className="font-medium">
                        Página {paginaAtual} de {totalPaginas}
                      </span>
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-[color:var(--text-muted)]">Itens por Página:</label>
                        <select
                          value={tamanhoPagina}
                          onChange={(e) => {
                            setTamanhoPagina(Number(e.target.value));
                            setPaginaAtual(1);
                          }}
                          className="border border-[color:var(--border)] rounded px-2 py-1 text-sm bg-[color:var(--surface)] hover:bg-[color:var(--surface-muted)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/35"
                        >
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                          <option value={200}>200</option>
                        </select>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          disabled={paginaAtual === 1}
                          onClick={() => setPaginaAtual((p) => Math.max(1, p - 1))}
                          className="px-3 py-1 border border-[color:var(--border)] rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[color:var(--surface-muted)] transition-colors text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/35"
                        >
                          Anterior
                        </button>
                        <button
                          disabled={paginaAtual === totalPaginas}
                          onClick={() => setPaginaAtual((p) => Math.min(totalPaginas, p + 1))}
                          className="px-3 py-1 border border-[color:var(--border)] rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[color:var(--surface-muted)] transition-colors text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/35"
                        >
                          Próxima
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  {!conciliacaoId && (
                    <button
                      onClick={salvarConciliacao}
                      disabled={salvando}
                      className="px-4 py-2 bg-[color:var(--accent)] text-white rounded-lg hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-75 transition-all shadow-sm hover:shadow-md flex items-center gap-2 font-medium whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/45"
                      title="Salvar Conciliação"
                    >
                      {salvando ? (
                        <>
                          <RefreshCw className="h-4 w-4 animate-spin" />
                          Salvando...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4" />
                          Salvar Conciliação
                        </>
                      )}
                    </button>
                  )}
                  {periodo && (
                    <div className="text-left sm:text-right">
                      <p className="text-xs text-[color:var(--text-muted)]">Período do extrato</p>
                      <p className="text-sm font-medium text-[color:var(--text-primary)] whitespace-nowrap">
                        {formatarData(periodo.data_inicio)} até {formatarData(periodo.data_fim)}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* KPIs - Reposicionados antes dos filtros */}
              {estatisticas && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-[color:var(--surface)] rounded-lg p-4 border border-[color:var(--border)] hover:shadow-md transition-all">
                    <p className="text-xs text-[color:var(--text-muted)] mb-2 font-medium">IXC - Pagas</p>
                    <p className="text-2xl font-bold text-[color:var(--text-primary)]">{estatisticas.total_ixc_pagas || 0}</p>
                  </div>
                  <div className="bg-[color:var(--surface)] rounded-lg p-4 border border-[color:var(--border)] hover:shadow-md transition-all">
                    <p className="text-xs text-[color:var(--text-muted)] mb-2 font-medium">IXC - Recebidas</p>
                    <p className="text-2xl font-bold text-[color:var(--text-primary)]">{estatisticas.total_ixc_recebidas || 0}</p>
                  </div>
                  <div className="bg-[color:var(--surface)] rounded-lg p-4 border border-[color:var(--border)] hover:shadow-md transition-all">
                    <p className="text-xs text-[color:var(--text-muted)] mb-2 font-medium">OFX - Transações</p>
                    <p className="text-2xl font-bold text-[color:var(--text-primary)]">{estatisticas.total_ofx || 0}</p>
                  </div>
                  <div className="bg-[color:var(--surface-muted)] rounded-lg p-4 border border-[color:var(--border)] hover:shadow-md transition-all">
                    <p className="text-xs text-[color:var(--accent)] mb-2 font-medium">Total Conciliado</p>
                    <p className="text-2xl font-bold text-[color:var(--text-primary)]">{estatisticas.total_conciliado || 0}</p>
                  </div>
                </div>
              )}
              
              {/* Abas e Filtros - design mais limpo */}
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {abas.map((aba) => (
                    <button
                      key={aba.id}
                      onClick={() => {
                        setAbaAtiva(aba.id);
                        setPaginaAtual(1);
                      }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        abaAtiva === aba.id
                          ? 'bg-[color:var(--accent)] text-white shadow-sm'
                          : 'bg-[color:var(--surface)] text-[color:var(--text-primary)] border border-[color:var(--border)] hover:bg-[color:var(--surface-muted)]'
                      }`}
                    >
                      {aba.label}
                      <span className={`ml-2 text-xs font-semibold ${
                        abaAtiva === aba.id ? 'text-white/80' : 'text-[color:var(--text-muted)]'
                      }`}>
                        {aba.count}
                      </span>
                    </button>
                  ))}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-[color:var(--text-muted)] font-medium whitespace-nowrap">Tipo:</label>
                    <select
                      value={filtroTipo}
                      onChange={(e) => {
                        setFiltroTipo(e.target.value);
                        setPaginaAtual(1);
                      }}
                      className="w-full px-3 py-2 rounded-lg bg-[color:var(--surface)] text-[color:var(--text-primary)] border border-[color:var(--border)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] text-sm"
                    >
                      <option value="todos">Todos</option>
                      <option value="credito">Crédito</option>
                      <option value="debito">Débito</option>
                    </select>
                  </div>
                  <div>
                    <input
                      type="text"
                      placeholder="Pesquisar por nome ou valor..."
                      value={termoPesquisaInput}
                      onChange={(e) => setTermoPesquisaInput(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg bg-[color:var(--surface)] text-[color:var(--text-primary)] border border-[color:var(--border)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>

          {/* Mobile: cards compactos */}
          <div className="md:hidden space-y-3">
            {dadosPagina.length === 0 ? (
              <div className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)] p-4 text-center text-sm text-[color:var(--text-muted)]">
                Nenhum registro encontrado para o filtro selecionado.
              </div>
            ) : (
              dadosPagina.map((item, index) => (
                <div
                  key={index}
                  onClick={() => abrirDetalhes(item)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      abrirDetalhes(item);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  className="w-full text-left rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)] p-3 space-y-2 hover:bg-[color:var(--surface-muted)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/35"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm font-semibold text-[color:var(--text-primary)]">
                      {formatarMoeda(item.valor)}
                    </div>
                    <div className="flex items-center gap-1">
                      {renderTipoBadge(item.tipo)}
                    </div>
                  </div>
                  <div className="text-xs text-[color:var(--text-muted)]">
                    {item.fornecedor || '-'}
                  </div>
                  <div className="flex items-center justify-between text-xs text-[color:var(--text-muted)]">
                    <span>{formatarData(item.data)}</span>
                    <span className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1">
                        <span className="sr-only">Tem no IXC:</span>
                        {item.tem_no_ixc ? (
                          <CheckCircle className="h-4 w-4 text-[color:var(--status-good)]" />
                        ) : (
                          <XCircle className="h-4 w-4 text-[color:var(--status-danger)]" />
                        )}
                        <span>{item.tem_no_ixc ? 'IXC ok' : 'IXC não'}</span>
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <span className="sr-only">Tem no OFX:</span>
                        {item.tem_no_ofx ? (
                          <CheckCircle className="h-4 w-4 text-[color:var(--status-good)]" />
                        ) : (
                          <XCircle className="h-4 w-4 text-[color:var(--status-danger)]" />
                        )}
                        <span>{item.tem_no_ofx ? 'OFX ok' : 'OFX não'}</span>
                      </span>
                    </span>
                  </div>

                  {abaAtiva !== 'conciliadas' && abaAtiva !== 'duplicidades' && (
                    <div className="flex justify-end pt-1">
                      {!item.tem_no_ixc && item.tem_no_ofx && item.categoria === 'pendencias_ofx' && (
                        <button
                          onClick={(e) => abrirModalManual(item, e)}
                          className="inline-flex min-h-[44px] items-center px-3 py-2 text-sm font-medium text-[color:var(--accent)] bg-[color:var(--surface-muted)] rounded hover:bg-[color:var(--surface)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/35"
                          title="Conciliar manualmente"
                        >
                          Conciliar
                        </button>
                      )}
                      {item.tem_no_ixc && !item.tem_no_ofx && item.categoria === 'pendencias_api' && (
                        <button
                          onClick={(e) => abrirModalManualPendenciasApi(item, e)}
                          className="inline-flex min-h-[44px] items-center px-3 py-2 text-sm font-medium text-[color:var(--accent)] bg-[color:var(--surface-muted)] rounded hover:bg-[color:var(--surface)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/35"
                          title="Conciliar manualmente"
                        >
                          Conciliar
                        </button>
                      )}
                      {item.manual && (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-[color:var(--surface-muted)] text-[color:var(--text-primary)] border border-[color:var(--border)]">
                          Manual
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          <div className="hidden md:block overflow-x-auto relative">
            <table className="min-w-full divide-y divide-[color:var(--border)]">
              <thead className="bg-[color:var(--surface-muted)]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider border-r border-[color:var(--border)]">
                    Valor, Data e Fornecedor
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider border-r border-[color:var(--border)]">
                    Tem no IXC
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider border-r border-[color:var(--border)]">
                    Tem no OFX
                  </th>
                  {abaAtiva !== 'conciliadas' && abaAtiva !== 'duplicidades' && (
                    <th className="px-6 py-3 text-center text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider">
                      Ações
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-[color:var(--surface)] divide-y divide-[color:var(--border)]">
                {dadosPagina.length === 0 ? (
                  <tr>
                    <td colSpan={abaAtiva === 'conciliadas' || abaAtiva === 'duplicidades' ? 3 : 4} className="px-6 py-8 text-center text-sm text-[color:var(--text-muted)]">
                      Nenhum registro encontrado para o filtro selecionado.
                    </td>
                  </tr>
                ) : (
                  dadosPagina.map((item, index) => (
                    <tr key={index} className="hover:bg-[color:var(--surface-muted)] transition-colors cursor-pointer" onClick={() => abrirDetalhes(item)}>
                      {/* Coluna 1: Valor, Data e Fornecedor */}
                      <td className="px-6 py-4 whitespace-nowrap border-r border-[color:var(--border)]">
                      <div className="space-y-1">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-sm font-semibold text-[color:var(--text-primary)]">
                            {formatarMoeda(item.valor)}
                          </div>
                          <div className="flex items-center gap-1">
                            {renderTipoBadge(item.tipo)}
                          </div>
                        </div>
                        <div className="text-xs text-[color:var(--text-muted)]">
                          {formatarData(item.data)}
                        </div>
                        <div className="text-xs text-[color:var(--text-muted)]">
                          {item.fornecedor || '-'}
                          </div>
                        </div>
                      </td>

                      {/* Coluna 2: Tem no IXC */}
                      <td className="px-6 py-4 whitespace-nowrap text-center border-r border-[color:var(--border)]">
                        {item.tem_no_ixc ? (
                          <div className="flex items-center justify-center">
                            <CheckCircle className="h-5 w-5 text-[color:var(--status-good)]" />
                          </div>
                        ) : (
                          <div className="flex items-center justify-center">
                            <XCircle className="h-5 w-5 text-[color:var(--status-danger)]" />
                          </div>
                        )}
                      </td>

                      {/* Coluna 3: Tem no OFX */}
                      <td className="px-6 py-4 whitespace-nowrap text-center border-r border-[color:var(--border)]">
                        {item.tem_no_ofx ? (
                          <div className="flex items-center justify-center">
                            <CheckCircle className="h-5 w-5 text-[color:var(--status-good)]" />
                          </div>
                        ) : (
                          <div className="flex items-center justify-center">
                            <XCircle className="h-5 w-5 text-[color:var(--status-danger)]" />
                          </div>
                        )}
                      </td>

                      {/* Coluna 4: Ações - Ocultar nas abas de conciliadas e duplicidades */}
                      {abaAtiva !== 'conciliadas' && abaAtiva !== 'duplicidades' && (
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          {/* Botão para Pendências OFX (tem OFX, não tem IXC) */}
                          {!item.tem_no_ixc && item.tem_no_ofx && item.categoria === 'pendencias_ofx' && (
                            <button
                              onClick={(e) => abrirModalManual(item, e)}
                              className="px-3 py-1 text-xs font-medium text-white bg-[color:var(--accent)] rounded hover:opacity-90 transition-colors"
                              title="Conciliar manualmente"
                            >
                              Conciliar
                            </button>
                          )}
                          {/* Botão para Pendências API (tem IXC, não tem OFX) */}
                          {item.tem_no_ixc && !item.tem_no_ofx && item.categoria === 'pendencias_api' && (
                            <button
                              onClick={(e) => abrirModalManualPendenciasApi(item, e)}
                              className="px-3 py-1 text-xs font-medium text-white bg-[color:var(--accent)] rounded hover:opacity-90 transition-colors"
                              title="Conciliar manualmente"
                            >
                              Conciliar
                            </button>
                          )}
                          {item.manual && (
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-[color:var(--surface-muted)] text-[color:var(--accent)] border border-[color:var(--border)]">
                              Manual
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          </>
        )}

        {/* Estado inicial - sem dados - dentro do mesmo card */}
        {!validando && dadosConciliacao.length === 0 && arquivoOFX && (
          <div className="p-8">
            <div className="text-center">
              <FileText className="h-12 w-12 text-[color:var(--text-muted)] mx-auto mb-4" />
              <p className="text-[color:var(--text-muted)]">
                Clique em "Processar e Validar" para iniciar a Conciliação
              </p>
            </div>
          </div>
        )}

        {/* Loading durante validação - dentro do mesmo card */}
        {validando && (
          <div className="p-6">
            <LoadingSpinner
              fullScreen={false}
              className="py-0"
              cardClassName="max-w-none w-full"
              label="Processando OFX"
              subtitle="Processando arquivo OFX e validando com dados do IXC..."
            />
          </div>
        )}
      </div>


      <ConciliacaoDetalhesModal 
        isOpen={modalAberto} 
        onClose={() => setModalAberto(false)} 
        item={itemSelecionado} 
      />

      <ConciliacaoManualModal
        isOpen={modalManualAberto}
        onClose={() => {
          setModalManualAberto(false);
          setItemOfxParaManual(null);
        }}
        onError={(message) => {
          showNotification(message, 'error');
        }}
        itemOfx={itemOfxParaManual?.ofx || itemOfxParaManual}
        contasIxcDisponiveis={
          itemOfxParaManual?.isPendenciaApi
            ? [itemOfxParaManual.ixc].filter(Boolean) // Para Pendências API, já temos a conta IXC selecionada
            : [...pendenciasApi.map(p => p.conta || p.ixc).filter(Boolean)] // Para Pendências OFX, mostrar todas as contas API disponíveis
        }
        contasOfxDisponiveis={
          itemOfxParaManual?.isPendenciaApi
            ? [...pendenciasOfx.map(p => p.transacao || p.ofx).filter(Boolean)] // Para Pendências API, mostrar Transações OFX disponíveis
            : []
        }
        isPendenciaApi={itemOfxParaManual?.isPendenciaApi || false}
        onConfirm={handleConciliacaoManualConfirmada}
        conciliacaoId={conciliacaoId}
      />

      {/* Modal de Seleção de Conta Bancária */}
      {modalContaAberto && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div 
              className="fixed inset-0 bg-black/55 transition-opacity" 
              onClick={() => {
                setModalContaAberto(false);
                setMensagemModalConta('');
              }}
            />
            <div className="relative inline-block w-full max-w-2xl p-6 my-8 text-left align-middle bg-[color:var(--surface)] rounded-lg shadow-xl transform transition-all">
              <div className="flex items-center justify-between pb-4 border-b border-[color:var(--border)]">
                <div>
                  <h3 className="text-lg font-semibold text-[color:var(--text-primary)]">
                    Selecione a Conta Bancária
                  </h3>
                  <p className="text-sm text-[color:var(--text-muted)] mt-1">
                    {mensagemModalConta || 'Não foi possível identificar automaticamente a conta do arquivo OFX.'}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setModalContaAberto(false);
                    setMensagemModalConta('');
                  }}
                  className="p-1 text-[color:var(--text-muted)] hover:text-[color:var(--text-muted)] rounded-full hover:bg-[color:var(--surface-muted)]"
                >
                  <XCircle className="h-5 w-5" />
                </button>
              </div>

              {identificadoresOfx && (
                <div className="mt-4 p-3 rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-muted)]">
                  <p className="text-sm font-medium text-[color:var(--text-primary)]">Identificadores do OFX:</p>
                  <div className="mt-1 text-xs text-[color:var(--accent)] space-y-1">
                    {identificadoresOfx.bankId && <p>Bank ID: {identificadoresOfx.bankId}</p>}
                    {identificadoresOfx.acctId && <p>Account ID: {identificadoresOfx.acctId}</p>}
                    {identificadoresOfx.fid && <p>FID: {identificadoresOfx.fid}</p>}
                  </div>
                </div>
              )}

              <div className="mt-4 max-h-[400px] overflow-y-auto">
                <div className="space-y-2">
                  {contasDisponiveis.map((conta) => (
                    <button
                      key={conta.id}
                      onClick={() => handleSelecionarConta(conta.id, conta)}
                      className="w-full p-4 text-left border border-[color:var(--border)] rounded-lg hover:border-[color:var(--accent)] hover:bg-[color:var(--surface-muted)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/35"
                    >
                      <div className="font-medium text-[color:var(--text-primary)]">{conta.descricao}</div>
                      <div className="text-sm text-[color:var(--text-muted)] mt-1">
                        {conta.banco && <span>Banco: {conta.banco}</span>}
                        {conta.conta && <span className="ml-3">Conta: {conta.conta}</span>}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-[color:var(--border)] flex justify-end">
                <button
                  onClick={() => {
                    setModalContaAberto(false);
                    setMensagemModalConta('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-[color:var(--text-primary)] bg-[color:var(--surface-muted)] rounded-lg hover:bg-[color:var(--surface-muted)]"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConciliacaoBancaria;
